import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createInformationCapitalGoal,
  createInformationCapitalIssue,
  fetchApplicationTypes,
  fetchBusinessProcesses,
  fetchStrategicGoals,
  fetchCustomerValues,
  fetchFinancialGoals,
  fetchStrategicIssues,
  fetchHumanCapitalGoals,
  fetchInfoCapitalGoalsApp,
  fetchInfoCapitalIssuesApp,
  fetchInfoCapitalGoalsIt,
  fetchInfoCapitalGoalsData,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

const GOAL_TYPE = "InformationCapitalGoalApp";
const ISSUE_TYPE = "InformationCapitalIssueApp";

export default function SmInfoCapitalGoalAppCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [kpiType, setKpiType] = useState("goal"); // "goal" | "issue"
  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedAppType, setSelectedAppType] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [selectedBusinessProcessId, setSelectedBusinessProcessId] = useState("");
  const [businessProcesses, setBusinessProcesses] = useState([]);
  const [appTypes, setAppTypes] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [humanCapitalGoals, setHumanCapitalGoals] = useState([]);
  const [infoCapitalGoalsApp, setInfoCapitalGoalsApp] = useState([]);
  const [infoCapitalIssuesApp, setInfoCapitalIssuesApp] = useState([]);
  const [infoCapitalGoalsIt, setInfoCapitalGoalsIt] = useState([]);
  const [infoCapitalGoalsData, setInfoCapitalGoalsData] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());
  const [issueSelectedIndicatorNames, setIssueSelectedIndicatorNames] = useState([]);

  // 指標は選択されたアプリタイプの indicators から取得
  const sourceIndicators = selectedAppType?.indicators || [];

  // 原因目標モード：選択したビジネスプロセスの指標
  const selectedBusinessProcess = businessProcesses.find((bp) => bp.businessProcessId === selectedBusinessProcessId) || null;
  const issueSourceIndicators = selectedBusinessProcess?.indicators || [];

  useEffect(() => {
    (async () => {
      try {
        const [atypes, bps, sgGoals, cvGoals, fgGoals, issues, hcGoals, icIt, icData, existingSfs, icIssuesApp] = await Promise.all([
          fetchApplicationTypes(businessDomainId),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchHumanCapitalGoals(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsIt(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsData(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
          fetchInfoCapitalIssuesApp(businessDomainId).catch(() => []),
        ]);
        setAppTypes(Array.isArray(atypes) ? atypes : []);
        setBusinessProcesses(Array.isArray(bps) ? bps : []);
        setStrategicGoals(Array.isArray(sgGoals) ? sgGoals : []);
        setCustomerValues(Array.isArray(cvGoals) ? cvGoals : []);
        setFinancialGoals(Array.isArray(fgGoals) ? fgGoals : []);
        setStrategicIssues(Array.isArray(issues) ? issues : []);
        setHumanCapitalGoals(Array.isArray(hcGoals) ? hcGoals : []);
        setInfoCapitalGoalsIt(Array.isArray(icIt) ? icIt : []);
        setInfoCapitalGoalsData(Array.isArray(icData) ? icData : []);
        setInfoCapitalGoalsApp(Array.isArray(existingSfs) ? existingSfs : []);
        setInfoCapitalIssuesApp(Array.isArray(icIssuesApp) ? icIssuesApp : []);
        const used = new Set(
          (Array.isArray(existingSfs) ? existingSfs : [])
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleKpiTypeChange = (e) => {
    setKpiType(e.target.value);
    setSelectedAppType(null);
    setSelectedNames([]);
    setSourceIds([]);
    setSelectedBusinessProcessId("");
    setIssueSelectedIndicatorNames([]);
  };

  const handleBpChange = (e) => {
    setSelectedBusinessProcessId(e.target.value);
    setIssueSelectedIndicatorNames([]);
  };

  const handleAppTypeChange = (e) => {
    const type = appTypes.find((at) => at.applicationTypeId === e.target.value);
    setSelectedAppType(type || null);
    setSelectedNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (kpiType === "issue") {
        const allGoals = [...infoCapitalGoalsApp, ...infoCapitalIssuesApp];
        const issueSources = sourceIds
          .map((id) => {
            const goal = allGoals.find((s) => s.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        const issueIndicators = issueSelectedIndicatorNames
          .map((name) => issueSourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        await createInformationCapitalIssue({
          successFactorName,
          successFactorDescription,
          businessDomainId,
          businessProcessId: selectedBusinessProcessId || undefined,
          successFactorType: ISSUE_TYPE,
          sources: issueSources,
          indicators: issueIndicators,
          applicationTasks: [],
        });
      } else {
        if (sourceIndicators.length > 0 && selectedNames.length === 0) {
          notify.error(t("financialGoal.indicatorRequired"));
          return;
        }
        const indicators = selectedNames
          .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        const allGoals = [...strategicIssues];
        const sources = sourceIds
          .map((id) => {
            const goal = allGoals.find((s) => s.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        await createInformationCapitalGoal({
          successFactorName,
          successFactorDescription,
          applicationTypeId: selectedAppType?.applicationTypeId || "",
          indicators,
          sources,
          businessDomainId,
          companyId,
          successFactorType: GOAL_TYPE,
        });
      }
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-app`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("infoCapitalGoalApp.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">KPI種類</FormLabel>
              <RadioGroup row value={kpiType} onChange={handleKpiTypeChange}>
                <FormControlLabel value="goal" control={<Radio />} label="結果目標" />
                <FormControlLabel value="issue" control={<Radio />} label="原因目標" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {kpiType === "issue" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("businessProcess.title")}</InputLabel>
              <Select
                value={selectedBusinessProcessId}
                label={t("businessProcess.title")}
                onChange={handleBpChange}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {businessProcesses.map((bp) => (
                  <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                    {bp.businessProcessName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}

          {kpiType === "goal" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("applicationType.label")}</InputLabel>
              <Select
                value={selectedAppType?.applicationTypeId || ""}
                label={t("applicationType.label")}
                onChange={handleAppTypeChange}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {appTypes.map((at) => (
                  <MenuItem key={at.applicationTypeId} value={at.applicationTypeId}>
                    {at.applicationTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}

          {/* ソース選択（結果目標モード：全種類） */}
          {kpiType === "goal" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
              >
                {strategicIssues.length > 0 && [
                  <MenuItem key="__si_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("strategicIssue.list.title")} ──
                  </MenuItem>,
                  ...strategicIssues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>}

          {/* ソース選択（原因目標モード：情報資本目標のみ） */}
          {kpiType === "issue" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
              >
                {infoCapitalGoalsApp.length > 0 && [
                  <MenuItem key="__icga_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalApp.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsApp.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalIssuesApp.length > 0 && [
                  <MenuItem key="__icai_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalIssueApp.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalIssuesApp.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>}

          {/* 指標選択（結果目標モード） */}
          {kpiType === "goal" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                    {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}

          {/* 指標選択（原因目標モード） */}
          {kpiType === "issue" && <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={issueSourceIndicators.length === 0}
                value={issueSelectedIndicatorNames}
                onChange={(e) => setIssueSelectedIndicatorNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {issueSourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                    {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>}
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-app`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
